import React, { useState } from "react";
import { Box, DialogContent, Grid, Typography } from "@mui/material";
import { connect } from "react-redux";
import { getText } from "../../../../core/Constant";
import { TextInputComponent, DialogComponent } from "../../../atom";
import TextButtonComponet from "../../../atom/Buttons/TextButton";
import ThumbnailUploadComponent from "../../../atom/Inputs/Thumbnail";
import { Actions } from "../../../../core/modules/Actions";
import { renderTruncatedText } from "../../../../core/Constant"; 

const AdminDocumentFolderAdd = ({
  onClose = () => {},
  courseId,
  current_document_id = null,
  createFolder,
  existingItems = [],
  parent_id = null,
  quizId = null,
  useQuizRoute = false,
  createQuizFolder,
}) => {
  const [folderName, setFolderName] = useState("");
  const [isFolderNameError, setIsFolderNameError] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [suggestedName, setSuggestedName] = useState("");
  const [isContextError, setIsContextError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);

  const onFolderNameChange = (e) => {
    setFolderName(getText(e));
    setIsFolderNameError(false);
    setIsContextError(false);
  };

  const onReset = () => {
    setFolderName("");
    setIsFolderNameError(false);
    setIsContextError(false);
    setDuplicateDialogOpen(false);
    setSuggestedName("");
    setThumbnail(null);
  };

  const onCancel = () => {
    onReset();
    onClose();
  };

  // Helper utility to auto-increment name counts safely until a completely unique identifier index is reached
  const generateDuplicateName = (initialName) => {
    let targetName = initialName.trim();
    let counter = 1;
    
    // Check folders specifically inside our current active level directory branch arrays
    const existingFolderNames = existingItems
      .filter(item => Number(item?.is_directory) === 1)
      .map(item => item.name.toLowerCase().trim());

    // Loop continuously until our mutation resolves safely away from collisions
    while (existingFolderNames.includes(targetName.toLowerCase())) {
      targetName = `${initialName.trim()}(${counter})`;
      counter++;
    }
    return targetName;
  };
  const onCreateFolder = () => {
    let hasError = false;
    if (!folderName?.trim()) {
      setIsFolderNameError(true);
      hasError = true;
    }

    const hasCourseId = courseId !== null;
    const hasParentId = parent_id !== null;

    if (!hasCourseId && !hasParentId) {
      setIsContextError(true);
      hasError = true;
    }

    if (hasError) return;

    const typedName = folderName.trim();
    
    // Check if folder name matches any directory string properties currently listed
    const isDuplicate = existingItems.some(
      (item) => Number(item?.is_directory) === 1 && item.name.toLowerCase().trim() === typedName.toLowerCase()
    );

    if (isDuplicate) {
      const autoGeneratedName = generateDuplicateName(typedName);
      setSuggestedName(autoGeneratedName);
      setDuplicateDialogOpen(true);
      return;
    }

    // If perfectly clean, execute basic creation sequence right away
    dispatchPayload(typedName);
  };

  const handleConfirmDuplicate = () => {
    setDuplicateDialogOpen(false);
    dispatchPayload(suggestedName);
  };

  const dispatchPayload = async (finalName) => {
    const fd = new FormData();
    fd.append("name", finalName);
    fd.append("course_id", courseId || "");
    fd.append("useQuizRoute", useQuizRoute);

    if (thumbnail instanceof File || thumbnail instanceof Blob) {
      fd.append("thumbnail", thumbnail);
    }

    if (useQuizRoute) {
      fd.append("parent_id", parent_id || "");
    } else if (current_document_id) {
      fd.append("current_document_id", current_document_id);
    }

    if (quizId) {
      fd.append("quiz_id", quizId);
    }

    try {
      setIsSubmitting(true);
      if (useQuizRoute) {
        await createQuizFolder(fd);
      } else {
        await createFolder(fd);
      }
    onReset();
    onClose();
    } catch (error) {
      console.error("Error creating folder:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <DialogContent>
      <Grid container spacing={2}>
          {/* Validation error message if neither course_id nor parent_id is present */}
          {isContextError && (
            <Grid item xs={12}>
              <Typography sx={{ color: "#d32f2f", fontSize: "14px", fontWeight: 500 }}>
                Please select a course or open a folder before creating a new folder.
              </Typography>
            </Grid>
          )}

        <Grid xs={12} item>
          <TextInputComponent
              sx={{ mb: 1, color: "#4E657C", fontSize: 18, fontWeight: 600 }}
           label={
                <span>
                  Folder Name <span style={{ color: '#d06060', marginLeft: 2 }}>*</span>
                </span>
              }
            placeholder="e.g. Clinical Records"
            value={folderName}
            isError={isFolderNameError}
            error="Please enter folder name"
            onchange={onFolderNameChange}
            required={true}
          />
        </Grid>

          {/* Folder Thumbnail Upload Component */}
          <Grid item xs={12} mt={1}>
            <ThumbnailUploadComponent
              value={thumbnail}
              onChange={(file) => setThumbnail(file)}
              label="Folder Thumbnail"
              description="PNG, JPG, JPEG up to 2MB"
            />
          </Grid>

        <Grid item xs={6} mt={2}>
          <Box style={{ padding: 10 }}>
            <TextButtonComponet
              onButtonClick={onCancel}
              text="Cancel"
              classStyle="btn btn-secondary"
                isDisabled={isSubmitting}
            />
          </Box>
        </Grid>
        <Grid item xs={6} mt={2}>
          <Box style={{ padding: 10 }}>
            <TextButtonComponet
              onButtonClick={onCreateFolder}
              text="Create"
                isDisabled={isSubmitting}
            />
          </Box>
        </Grid>
      </Grid>
    </DialogContent>

      {/* Duplicate Interception Confirmation Prompt Dialog Layout */}
      <DialogComponent
        isShowCloseButton={false}
        title={"Duplicate Folder Name"}
        open={duplicateDialogOpen}
        maxwidth="xs"
        onClose={() => setDuplicateDialogOpen(false)}
      >
        <Box sx={{ px: 3, pb: 3, pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ color: "#2D3945", fontSize: 15, lineHeight: 1.5, wordBreak: "break-word" }}>
            A folder with the name <strong>"{renderTruncatedText(folderName.trim(), { limit: 25 })}"</strong> already exists in this location.
            <br /><br />
            Do you want to create a duplicate folder named <strong>"{renderTruncatedText(suggestedName, { limit: 25 })}"</strong>{" "} instead?
          </Typography>
          
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
            <TextButtonComponet
              text="Cancel"
              classStyle="btn btn-secondary"
              onButtonClick={() => setDuplicateDialogOpen(false)}
              isDisabled={isSubmitting}
            />
            <TextButtonComponet
              text="Yes, Duplicate"
              onButtonClick={handleConfirmDuplicate}
              isDisabled={isSubmitting}
            />
          </Box>
        </Box>
      </DialogComponent>
    </>
  );
};

export default connect(null, {
  createFolder: Actions.document.createFolder,
  createQuizFolder: Actions.quizes.createQuizFolder,
})(AdminDocumentFolderAdd);
